import React, { useState, useRef, useEffect } from "react";
import PayRatesForm from "./PayRatesSelect";
import WorkPeriodForm from "./WorkPeriod";
import LocationSelector from "./LocationSelector";
import LeaveAddComponent from "./LeaveAdd";

import api from "../../Utils/api";
import { toast } from "react-toastify";
import TrainingAccordion from "./TrainingItem";
import AddTrainingDialog from "./AddTrainDialog";
import TrainingList from "./TrainingList";
import Spinner from "../Spinner";

export default function EmploymentDetails({
  id,
  open,
  onClose,
  initialData,
  locations,
}) {
  // Refs for scrolling
  const workDetailsRef = useRef(null);
  const payDetailsRef = useRef(null);
  const regularHoursRef = useRef(null);
  const leaveEntitlementsRef = useRef(null);
  const [user, setUser] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("work");
  const [isLoading, setIsLoading] = useState(false);

  // Main employment data state (CRUD)
  const [employmentData, setEmploymentData] = useState({
    // * Work details
    deputyAccessLevel: "system_administrator",
    locations: {
      primaryLocation: "",
      subLocations: [],
    },
    hiredOn: "",
    trainings: [],

    // * Pay details
    employmentType: "full_time",
    payrollId: "",
    payRates: {
      payRateType: "hourly_rate",
      weekday: 0,
      saturday: 0,
      sunday: 0,
      publicHoliday: 0,
    },

    // * Regular working hours
    workPeriod: {
      period: "",
      day: "",
    },
    hoursPerPeriod: "",
    stressProfile: "24/7",

    // * Leave entitlements
    leaveEntitlements: [],
  });

  // For edit mode (optional)
  const [isEditing, setIsEditing] = useState(!initialData);

  // Load initial data if provided
  useEffect(() => {
    if (user) {
      setEmploymentData((prev) => ({ ...prev, ...user }));
      setIsEditing(false);
    }
  }, [user]);

  useEffect(() => {
    if (id !== "") getUserProfile();
  }, [id]);

  const getUserProfile = async () => {
    setIsLoading(true);
    const res = await api.get(`/user/${id}`);
    setIsLoading(false);
    if (res.data.success) {
      setUser(res.data.user);
    } else {
      console.error("Failed to fetch user profile");
    }
  };

  if (!open) return null;

  // Tab click handler
  const handleTabClick = (tab, ref) => {
    setActiveTab(tab);
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Controlled field change handler
  const handleChange = (field, value) => {
    setEmploymentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // CRUD Handlers (implement API calls as needed)
  const handleSave = async () => {
    // console.log(employmentData);
    setIsLoading(true);
    const res = await api.post("/user/update", {
      _id: id,
      updatedData: employmentData,
    });
    setIsLoading(false);

    if (res.data.success) {
      toast.success(res.data.message);
    } else {
      toast.error(res.data.message);
    }

    setIsEditing(false);
    // onClose();
  };

  const handleEdit = () => setIsEditing(true);

  const handleDelete = () => {
    // TODO: Call API to delete employment
    // Example: api.delete(`/employment/${id}`)
    onClose();
  };

  const backToPeopleList = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-white">
      {isLoading && <Spinner />}
      <div className="w-full max-w-6xl bg-white">
        <div className="bg-white mx-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold mt-2">Employment details</h2>
              <div className="text-lg text-gray-700">
                {employmentData.preferredName || "Employee Name"}
              </div>
            </div>
            <div>
              <button
                onClick={backToPeopleList}
                className="px-6 py-2 rounded bg-slate-200 text-gray-700 mr-2 font-semibold"
              >
                Back
              </button>

              <button
                onClick={handleSave}
                className="px-6 py-2 mr-2 rounded bg-slate-600 text-white font-semibold"
              >
                Save
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-8 border-b border-gray-300 mb-8">
            <button
              className={`pb-2 font-semibold border-b-2 transition-colors duration-200
                ${
                  activeTab === "work"
                    ? "border-slate-600 text-slate-600"
                    : "border-transparent text-gray-500 hover:text-slate-600"
                }
              `}
              onClick={() => handleTabClick("work", workDetailsRef)}
            >
              Work details
            </button>
            <button
              className={`pb-2 font-semibold border-b-2 transition-colors duration-200
                ${
                  activeTab === "pay"
                    ? "border-slate-600 text-slate-600"
                    : "border-transparent text-gray-500 hover:text-slate-600"
                }
              `}
              onClick={() => handleTabClick("pay", payDetailsRef)}
            >
              Pay details
            </button>
            <button
              className={`pb-2 font-semibold border-b-2 transition-colors duration-200
                ${
                  activeTab === "regular"
                    ? "border-slate-600 text-slate-600"
                    : "border-transparent text-gray-500 hover:text-slate-600"
                }
              `}
              onClick={() => handleTabClick("regular", regularHoursRef)}
            >
              Regular working hours
            </button>
            <button
              className={`pb-2 font-semibold border-b-2 transition-colors duration-200
                ${
                  activeTab === "leave"
                    ? "border-slate-600 text-slate-600"
                    : "border-transparent text-gray-500 hover:text-slate-600"
                }
              `}
              onClick={() => handleTabClick("leave", leaveEntitlementsRef)}
            >
              Leave entitlements
            </button>
          </div>
        </div>

        <div className="h-[60vh] px-8 overflow-y-auto">
          {/* Work Details Section */}
          <section className="mb-8">
            <h3 ref={workDetailsRef} className="font-semibold text-2xl mb-8">
              Work details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">
                  Access level
                </label>
                <p className="text-xs text-gray-500 ">
                  A set of permissions that control what a team member can do in
                  Roster.
                </p>
              </div>
              <div className="col-span-1">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  value={employmentData.deputyAccessLevel}
                  onChange={(e) =>
                    handleChange("deputyAccessLevel", e.target.value)
                  }
                >
                  <option value="system_administrator">
                    System Administrator
                  </option>
                  <option value="employee">Employee</option>
                  <option value="location_manager">Location Manager</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">Works at</label>
                <p className="text-xs text-gray-500 ">
                  Locations where a team member can be scheduled.
                </p>
              </div>
              <div className="col-span-2">
                <LocationSelector
                  value={employmentData.locations}
                  locations={locations.map((item) => ({
                    name: item.name,
                    _id: item._id,
                  }))}
                  onChange={(newLocations) =>
                    setEmploymentData((prev) => ({
                      ...prev,
                      locations: newLocations,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">Hired on</label>
              </div>
              <div className="">
                <input
                  type="date"
                  className="w-full  rounded border-gray-300 bg-slate-100 p-2"
                  value={employmentData.hiredOn?.slice(0, 10)}
                  onChange={(e) => handleChange("hiredOn", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div className="">
                <label className="block text-sm font-medium">Training</label>
                <p className="text-xs text-gray-500 ">
                  Training allows a team member to work in areas with training
                  requirements.
                </p>
              </div>
              <div className="col-span-2">
                <TrainingList
                  onChange={(trs) => handleChange("trainings", trs)}
                  trainingsData={employmentData.trainings}
                />
              </div>
            </div>
          </section>

          {/* Pay Details Section */}
          <section ref={payDetailsRef} className="mb-8">
            <h3 className="font-semibold text-2xl my-8">Pay details</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">
                  Employment type
                </label>
              </div>
              <div className="col-span-1">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  value={employmentData.employmentType}
                  onChange={(e) =>
                    handleChange("employmentType", e.target.value)
                  }
                >
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="casual">Casual</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
            </div>
            <PayRatesForm
              value={employmentData.payRates}
              onChange={(val) =>
                setEmploymentData((prev) => ({ ...prev, payRates: val }))
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">Payroll ID</label>
                <p className="text-xs text-gray-500 ">
                  A unique identifier used for payroll.
                </p>
              </div>
              <div className="col-span-1">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  value={employmentData.payrollId}
                  onChange={(e) => handleChange("payrollId", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Regular Working Hours Section */}
          <section ref={regularHoursRef} className="mb-8">
            <h3 className="font-semibold text-2xl my-8">
              Regular working hours
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="col-span-3">
                <p className="text-sm bg-gray-100 rounded-md p-4 mb-4">
                  <span className="mr-2">✨</span> Keep track of your team
                  member's hours by setting up their regular working hours and
                  choose how overtime applies.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">Work period</label>
                <p className="text-xs text-gray-500 ">
                  The period used to calculate overtime. This overrides the
                  default location setting.
                </p>
              </div>
              <div className="col-span-2">
                <WorkPeriodForm
                  value={employmentData.workPeriod}
                  onChange={(val) =>
                    setEmploymentData((prev) => ({ ...prev, workPeriod: val }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 mt-4 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">
                  Hours per work period
                </label>
                <p className="text-xs text-gray-500 ">
                  How many hours they are expected to work in the work period.
                </p>
              </div>
              <div className="col-span-1">
                <input
                  type="number"
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  placeholder="0 hours"
                  value={employmentData.hoursPerPeriod}
                  onChange={(e) =>
                    handleChange("hoursPerPeriod", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium">
                  Stress profile
                </label>
                <p className="text-xs text-gray-500 ">
                  Scheduling rules such as maximum regular working hours and
                  minimum hours between shifts.
                </p>
              </div>
              <div className="col-span-1">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  value={employmentData.stressProfile}
                  onChange={(e) =>
                    handleChange("stressProfile", e.target.value)
                  }
                >
                  <option value="2_days_per_week">2 days per week</option>
                  <option value="24_7">24/7</option>
                  <option value="overtime_40">
                    CA Overtime 40hrs per week, 8hrs per day, max 6 days per
                    week
                  </option>
                  <option value="max_20_hours">Max 20 hours per week</option>
                  <option value="normal_38_hours_per_week">
                    Normal 38 hours per week
                  </option>
                  <option value="standard_40">
                    Standard 40 hours, 8 hours per day
                  </option>
                  {/* Other options */}
                </select>
              </div>
            </div>
          </section>

          {/* Leave Entitlements Section */}
          <section ref={leaveEntitlementsRef}>
            <h3 className="font-semibold text-2xl my-8">Leave entitlements</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="mb-4">
                <label className="block text-sm font-medium">
                  Leave entitlements
                </label>
                <p className="text-xs text-gray-500 ">
                  Scheduling rules such as maximum regular working hours and
                  minimum hours between shifts.
                </p>
              </div>
              <div className="col-span-1">
                <LeaveAddComponent
                  value={employmentData.leaveEntitlements}
                  onChange={(val) => handleChange("leaveEntitlements", val)}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
